import { DEFAULT_WEBSITE_CONTENT } from '../config/defaults';
import { API_BASE_URL } from './apiBase';
import { HomepageAboutStored } from './homepageAbout';
import { footerModel, sanitizeWhereIShootLinks } from './localBusiness';

export interface WhereIShootLink {
  label: string;
  href: string;
}

const STORAGE_KEY = 'website_content';
const POLL_MS = 10000;

export interface WebsiteContent {
  studioName: string;
  heroDescription: string;
  aboutDescription: string;
  contactEmail: string;
  contactPhone: string;
  contactLocation: string;
  footerLocality: string;
  areasServed: string;
  reviewLabel: string;
  reviewUrl: string;
  whereIShootHeading: string;
  whereIShootBody: string;
  whereIShootLinks: WhereIShootLink[];
  homepageAbout?: HomepageAboutStored | null;
  updatedAt?: string;
}

type ContentListener = (content: WebsiteContent) => void;

function textOrFallback(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function textOrDefault(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed || fallback;
}

function whereIShootLinks(value: unknown): WhereIShootLink[] {
  const sanitized = sanitizeWhereIShootLinks(value) as WhereIShootLink[] | null;
  if (sanitized) return sanitized;
  return DEFAULT_WEBSITE_CONTENT.whereIShootLinks.map((link) => ({ ...link }));
}

export function mergeWebsiteContent(parsed?: Partial<WebsiteContent> | null): WebsiteContent {
  const source = parsed || {};
  const content: WebsiteContent = {
    studioName: textOrFallback(source.studioName, DEFAULT_WEBSITE_CONTENT.studioName),
    heroDescription: textOrFallback(source.heroDescription, DEFAULT_WEBSITE_CONTENT.heroDescription),
    aboutDescription: textOrFallback(source.aboutDescription, DEFAULT_WEBSITE_CONTENT.aboutDescription),
    contactEmail: textOrFallback(source.contactEmail, DEFAULT_WEBSITE_CONTENT.contactEmail),
    contactPhone: textOrFallback(source.contactPhone, DEFAULT_WEBSITE_CONTENT.contactPhone),
    contactLocation: textOrFallback(source.contactLocation, DEFAULT_WEBSITE_CONTENT.contactLocation),
    footerLocality: textOrDefault(source.footerLocality, DEFAULT_WEBSITE_CONTENT.footerLocality),
    areasServed: textOrDefault(source.areasServed, DEFAULT_WEBSITE_CONTENT.areasServed),
    reviewLabel: textOrDefault(source.reviewLabel, DEFAULT_WEBSITE_CONTENT.reviewLabel),
    reviewUrl: footerModel({
      reviewUrl: textOrDefault(source.reviewUrl, DEFAULT_WEBSITE_CONTENT.reviewUrl),
    }).reviewUrl,
    whereIShootHeading: textOrDefault(source.whereIShootHeading, DEFAULT_WEBSITE_CONTENT.whereIShootHeading),
    whereIShootBody: textOrDefault(source.whereIShootBody, DEFAULT_WEBSITE_CONTENT.whereIShootBody),
    whereIShootLinks: whereIShootLinks(source.whereIShootLinks),
  };
  if (source.homepageAbout && typeof source.homepageAbout === 'object') {
    content.homepageAbout = source.homepageAbout;
  }
  if (typeof source.updatedAt === 'string') content.updatedAt = source.updatedAt;
  return content;
}

export function readLocalWebsiteContent(): WebsiteContent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return mergeWebsiteContent(JSON.parse(raw));
  } catch (error) {
    console.error('Error reading local website content:', error);
    return null;
  }
}

function rememberContent(content: WebsiteContent) {
  try {
    const nextJson = JSON.stringify(content);
    if (localStorage.getItem(STORAGE_KEY) === nextJson) return;
    localStorage.setItem(STORAGE_KEY, nextJson);
  } catch (error) {
    console.error('Error caching website content:', error);
  }
}

let currentContent = readLocalWebsiteContent() || mergeWebsiteContent(null);
const listeners = new Set<ContentListener>();
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pendingSaveJson: string | null = null;
let saveGeneration = 0;

function cacheAndNotify(content: WebsiteContent) {
  currentContent = content;
  rememberContent(content);
  listeners.forEach((listener) => listener(content));
}

export function getCurrentWebsiteContent(): WebsiteContent {
  return currentContent;
}

function stripServerMeta(content: Partial<WebsiteContent> & { _id?: string; _singleton?: boolean }) {
  const { _id, _singleton, ...rest } = content;
  return rest;
}

export async function fetchPublishedWebsiteContent(): Promise<WebsiteContent | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch(`${API_BASE_URL}/website-content?v=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.content || typeof data.content !== 'object') return null;
    return mergeWebsiteContent(stripServerMeta(data.content));
  } catch (error) {
    console.warn('Failed to load published website content:', error);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function refreshFromServer() {
  if (pendingSaveJson) return;
  const generation = saveGeneration;
  const remote = await fetchPublishedWebsiteContent();
  if (pendingSaveJson || generation !== saveGeneration || !remote) return;
  cacheAndNotify(remote);
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') refreshFromServer();
}

function handleStorage(event: StorageEvent) {
  if (event.key !== STORAGE_KEY || !event.newValue || pendingSaveJson) return;
  if (event.newValue === JSON.stringify(currentContent)) return;
  try {
    cacheAndNotify(mergeWebsiteContent(JSON.parse(event.newValue)));
  } catch (error) {
    console.error('Error applying website content from another tab:', error);
  }
}

function handleLocalUpdate(event: Event) {
  const detail = (event as CustomEvent<WebsiteContent>).detail;
  if (!detail || pendingSaveJson) return;
  const next = mergeWebsiteContent(detail);
  if (JSON.stringify(next) === JSON.stringify(currentContent)) return;
  cacheAndNotify(next);
}

export function subscribeWebsiteContent(listener: ContentListener) {
  listeners.add(listener);
  listener(currentContent);

  if (listeners.size === 1) {
    refreshFromServer();
    pollTimer = setInterval(refreshFromServer, POLL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('website_content_updated', handleLocalUpdate);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('website_content_updated', handleLocalUpdate);
    }
  };
}

export async function savePublishedWebsiteContent(content: Partial<WebsiteContent>): Promise<boolean> {
  const previous = currentContent;
  const merged = mergeWebsiteContent({
    ...content,
    updatedAt: new Date().toISOString(),
  });
  const generation = ++saveGeneration;
  pendingSaveJson = JSON.stringify(merged);
  cacheAndNotify(merged);
  window.dispatchEvent(new CustomEvent('website_content_updated', { detail: merged }));

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(`${API_BASE_URL}/website-content`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(merged),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`Save failed with status ${res.status}`);

    const data = await res.json().catch(() => ({}));
    if (generation !== saveGeneration) return true;

    pendingSaveJson = null;
    if (data?.content) {
      cacheAndNotify(mergeWebsiteContent(stripServerMeta(data.content)));
    }
    return true;
  } catch (error) {
    console.error('Error publishing website content:', error);
    if (generation === saveGeneration) {
      pendingSaveJson = null;
      const remote = await fetchPublishedWebsiteContent();
      cacheAndNotify(remote || previous);
    }
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}
